import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import HeroSection from './components/HeroSection';
import FeaturedEvents from './components/FeaturedEvents';
import HowItWorks from './components/HowItWorks';
import Categories from './components/Categories';
import FeaturedStores from './components/FeaturedStores';
import Testimonials from './components/Testimonials';
import CTASection from './components/CTASection';
import PricingSection from './components/PricingSection';
import Footer from './components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

export default function HomePage() {
  const navigate = useNavigate();

  // Schema.org JSON-LD
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebSite',
          '@id': `${SITE_URL}/#website`,
          url: SITE_URL,
          name: 'EVENTSHOWCASE',
          description: 'Discover events and grow your social following on EVENTSHOWCASE — the premier event discovery and management platform.',
          inLanguage: 'en-US',
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: `${SITE_URL}/discover?q={search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
          },
        },
        {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: 'EVENTSHOWCASE',
          url: SITE_URL,
          logo: {
            '@type': 'ImageObject',
            '@id': `${SITE_URL}/#logo`,
            url: 'https://public.readdy.ai/ai/img_res/35af317c-ff06-4831-b241-787dd1236a773.png',
            width: 200,
            height: 60,
            caption: 'EVENTSHOWCASE',
          },
          sameAs: [
            'https://instagram.com/eventshowcase',
            'https://twitter.com/eventshowcase',
            'https://linkedin.com/company/eventshowcase',
          ],
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer support',
            email: 'support@eventshowcase.de',
            availableLanguage: ['English', 'German'],
          },
        },
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/#webpage`,
          url: SITE_URL,
          name: 'EVENTSHOWCASE – Discover Events & Grow Your Social Following',
          description: 'Find free and paid events, boost social media engagement, and connect with event organizers worldwide on EVENTSHOWCASE.',
          isPartOf: { '@id': `${SITE_URL}/#website` },
          publisher: { '@id': `${SITE_URL}/#organization` },
          inLanguage: 'en-US',
          dateModified: '2026-04-05',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
            ],
          },
        },
        {
          '@type': 'SoftwareApplication',
          '@id': `${SITE_URL}/#app`,
          name: 'EVENTSHOWCASE',
          url: SITE_URL,
          applicationCategory: 'EventManagement',
          operatingSystem: 'Web',
          offers: [
            {
              '@type': 'Offer',
              name: 'Starter Plan',
              price: '0',
              priceCurrency: 'USD',
              description: 'Free plan for new event organizers with basic features.',
            },
            {
              '@type': 'Offer',
              name: 'Growth Plan',
              price: '19',
              priceCurrency: 'USD',
              description: 'Growth plan with lower commission rates and advanced features.',
            },
            {
              '@type': 'Offer',
              name: 'Pro Plan',
              price: '49',
              priceCurrency: 'USD',
              description: 'Pro plan with the lowest commission rates and full feature access.',
            },
          ],
        },
        {
          '@type': 'FAQPage',
          '@id': `${SITE_URL}/#faq`,
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How do I create an event on EVENTSHOWCASE?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Sign up for a free account, click "Create Event", fill in your event details including title, date, location, and pricing, then publish. Your event will be live immediately.',
              },
            },
            {
              '@type': 'Question',
              name: 'Is EVENTSHOWCASE free to use?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, EVENTSHOWCASE offers a free Starter plan. Paid plans (Growth and Pro) offer lower commission rates and advanced features for serious organizers.',
              },
            },
            {
              '@type': 'Question',
              name: 'How does the social follow gate work?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Organizers can require attendees to follow their social media accounts before receiving their event ticket. This helps organizers grow their audience while providing value to attendees.',
              },
            },
            {
              '@type': 'Question',
              name: 'How do I get paid as an event organizer?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Earnings from paid events are held briefly then released to your dashboard. You can withdraw via Paystack, Flutterwave, or PayPal depending on your region.',
              },
            },
          ],
        },
      ],
    };

    const existing = document.getElementById('schema-home');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-home';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    document.title = 'EVENTSHOWCASE – Discover Events & Grow Your Social Following';

    return () => {
      const el = document.getElementById('schema-home');
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;

    if (!hash && !search) return;

    const hashParams = new URLSearchParams(hash.replace('#', ''));
    const searchParams = new URLSearchParams(search);

    const type = hashParams.get('type') || searchParams.get('type');
    const accessToken = hashParams.get('access_token');
    const errorCode = hashParams.get('error_code') || searchParams.get('error_code');
    const code = searchParams.get('code');
    const token = searchParams.get('token');

    const hasAuthParams =
      accessToken ||
      errorCode ||
      type ||
      code ||
      token ||
      searchParams.get('error_code');

    if (hasAuthParams) {
      window.location.replace(`/confirm-email${search}${hash}`);
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HeroSection />
      <FeaturedEvents />
      <HowItWorks />
      <Categories />
      <FeaturedStores />
      <Testimonials />
      <PricingSection />
      <CTASection />
      <Footer />
    </div>
  );
}